import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  LucideAngularModule,
  Home,
  User,
  FileText,
  Calendar,
  ChevronDown,
  Settings,
  LogIn,
  Menu,
  X,
  Globe,
  LogOut,
} from 'lucide-angular';
import { environment } from '../../../../environments/environment';
import { TranslationService } from '../../../core/services/translation.service';
import { ModalService } from '../../../core/services/modal.service';
import { AuthService } from '../../../core/services/auth.service';
import { ThemeService, Theme } from '../../../core/services/theme.service';
import { LoginModalComponent } from '../modal/login-modal/login-modal.component';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as AuthActions from '../../../store/auth/auth.actions';
import {
  selectIsAuthenticated,
  selectUserData,
  selectCurrentUser,
} from '../../../store/auth/auth.selectors';
import { User as UserInterface } from '../../../core/interfaces/user.interface';
import { selectCountries } from '../../../store/referentiel/referentiel.selectors';
import { loadCountries } from '../../../store/referentiel/referentiel.actions';
import {
  selectPassportMenu,
  selectEnableRegistration,
} from '../../../store/settings/settings.selectors';
import { Country } from '../../../core/interfaces/doctor.interface';
import { CountryStorageService } from '../../../core/services/country.storage.service';
import { UrlService } from '../../../core/services/url.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule, TranslateModule],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  readonly HomeIcon = Home;
  readonly UserIcon = User;
  readonly FileTextIcon = FileText;
  readonly CalendarIcon = Calendar;
  readonly SettingsIcon = Settings;
  readonly LogInIcon = LogIn;
  readonly LogOutIcon = LogOut;
  readonly MenuIcon = Menu;
  readonly XIcon = X;
  readonly GlobeIcon = Globe;
  readonly ChevronDownIcon = ChevronDown;

  currentRoute: string = '';
  isPassportRoute: boolean = false;
  private routerSubscription?: Subscription;

  // Store observables
  isLoggedIn$: any;
  userData$!: Observable<UserInterface | null>;
  enableRegistration$: Observable<boolean>;

  countries$: Observable<Country[] | null>;
  selectedCountry: Country | null = null;
  PrefixUrlCountryCode: string | null | undefined = null;
  showCountryDropdown = false;
  private destroy$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private translationService: TranslationService,
    private router: Router,
    private modalService: ModalService,
    private authService: AuthService,
    private store: Store,
    private countryStorage: CountryStorageService,
    private urlService: UrlService,
    private themeService: ThemeService,
  ) {
    this.countries$ = this.store.select(selectCountries);
    this.enableRegistration$ = this.store.select(selectEnableRegistration);

    // Créer un observable qui combine les settings et génère les menus privés visibles
    this.privateMenuItems$ = this.store.select(selectPassportMenu).pipe(
      map((passportMenu) => {
        // Toujours inclure le menu HOME en premier
        const menuItems = [this.homeMenuItem];

        if (!passportMenu || passportMenu.length === 0) {
          // Si pas de settings, retourner HOME + tous les menus par défaut
          return [...menuItems, ...Object.values(this.privateMenuConfig)];
        }

        // Filtrer et mapper les menus selon les settings
        const visibleMenus = passportMenu
          .filter((menu) => menu.isVisible)
          .map((menu) => {
            const config = this.privateMenuConfig[menu.title];
            if (config) {
              return {
                ...config,
                order: menu.order,
              };
            }
            return null;
          })
          .filter(
            (item): item is { label: string; path: string; order: number } =>
              item !== null,
          )
          .sort((a, b) => a.order - b.order)
          .map(({ order, ...item }) => item); // Retirer order du résultat final

        return [...menuItems, ...visibleMenus];
      }),
    );
  }

  ngOnInit(): void {
    // Initialiser les observables du store
    this.isLoggedIn$ = this.store.select(selectIsAuthenticated);
    this.userData$ = this.store.select(selectCurrentUser);

    this.countryStorage.country$
      .pipe(takeUntil(this.destroy$))
      .subscribe((country) => {
        this.selectedCountry = country;
        this.PrefixUrlCountryCode = `/${this.selectedCountry?.code?.toLowerCase()}`;
      });

    // Set initial route
    this.updateRouteState(this.router.url);

    // Subscribe to route changes
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updateRouteState(event.urlAfterRedirects || event.url);
      });

    this.store
      .select(selectCountries)
      .pipe(takeUntil(this.destroy$))
      .subscribe((countries) => {
        if (!countries || countries.length === 0) {
          this.store.dispatch(loadCountries());

          if (
            typeof window !== 'undefined' &&
            typeof localStorage !== 'undefined'
          ) {
            const saved = localStorage.getItem('defaultCountry');
            if (saved) {
              try {
                this.selectedCountry = JSON.parse(saved);
                this.PrefixUrlCountryCode = `/${this.selectedCountry?.code?.toLocaleLowerCase()}`;
              } catch {
                this.selectedCountry = null;
              }
            }
          }
        } else {
          // Mettre à jour le pays Sénégal avec les vraies données si on est sur Orange
          this.countryStorage.updateSenegalFromCountries(countries);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateRouteState(url: string) {
    this.currentRoute = url;
    this.isPassportRoute = url.includes('/passport');
  }

  isMenuOpen = false;
  isLanguageDropdownOpen = false;

  menuItems: { label: string; href: string }[] = [
    { label: 'Accueil', href: '/home' },
    { label: 'Médecins', href: '/medecins' },
    { label: 'Structures de santé', href: '/structures' },
    { label: 'Pharmacies', href: '/pharmacies' },
  ];

  navigationItems = [
    { label: 'Accueil', path: '', icon: this.HomeIcon },
    { label: 'Profil', path: '/profile', icon: this.UserIcon },
    { label: 'Documents', path: '/documents', icon: this.FileTextIcon },
    { label: 'Rendez-vous', path: '/appointments', icon: this.CalendarIcon },
    { label: 'Paramètres', path: '/settings', icon: this.SettingsIcon },
  ];

  publicMenuItems = [
    { label: 'MENUITEMS.PUBLIC.HOME', path: '/home' },
    { label: 'MENUITEMS.PUBLIC.PROFESSIONALS', path: '/medecins' },
    { label: 'MENUITEMS.PUBLIC.FACILITIES', path: '/structures' },
    { label: 'MENUITEMS.PUBLIC.PHARMACIES', path: '/pharmacies' },
  ];

  // Configuration des menus privés disponibles
  private readonly privateMenuConfig: Record<
    string,
    { label: string; path: string }
  > = {
    APPOINTMENTS: {
      label: 'MENUITEMS.PRIVATE.HEALTH_TRACKING',
      path: '/passport/health-tracking',
    },
    PATIENT_RECORD: {
      label: 'MENUITEMS.PRIVATE.PATIENT_RECORD',
      path: '/passport/patient-record',
    },
    INSURANCES: {
      label: 'MENUITEMS.PRIVATE.INSURANCES',
      path: '/passport/insurances',
    },
    AUTHORIZATIONS: {
      label: 'MENUITEMS.PRIVATE.AUTHORIZATIONS',
      path: '/passport/authorizations',
    },
    PAYMENTS: {
      label: 'MENUITEMS.PRIVATE.PAYMENTS',
      path: '/passport/payments',
    },
  };

  // Menu HOME toujours visible
  private readonly homeMenuItem = {
    label: 'MENUITEMS.PRIVATE.HOME',
    path: '/passport/dashboard',
  };

  // Observable pour les menus privés filtrés selon les settings
  privateMenuItems$: Observable<Array<{ label: string; path: string }>>;

  get currentLanguage(): string {
    return this.translationService.getCurrentLanguage();
  }

  get availableLanguages(): string[] {
    return this.translationService.getAvailableLanguages();
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu() {
    this.isMenuOpen = false;
  }

  toggleLanguageDropdown() {
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
  }

  closeLanguageDropdown() {
    this.isLanguageDropdownOpen = false;
  }

  isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  getUserData(): any {
    return this.authService.getUserData();
  }

  logout() {
    // Dispatcher l'action logout qui déclenchera l'effet avec la requête API
    this.store.dispatch(AuthActions.logout());
  }

  /**
   * Check if a route is active
   */
  isActiveRoute(path: string): boolean {
    const fullPath = `${this.PrefixUrlCountryCode}${path}`;
    return this.currentRoute === fullPath;
  }

  /**
   * Toggle between French and English
   */
  toggleLanguage(): void {
    this.translationService.toggleLanguage();
  }

  /**
   * Set specific language
   */
  setLanguage(language: string): void {
    this.translationService.setLanguage(language);
    this.closeLanguageDropdown();
  }

  /**
   * Get language display name
   */
  getLanguageName(languageCode: string): string {
    return this.translationService.getLanguageName(languageCode);
  }

  /**
   * Open login modal
   */
  openLoginModal(): void {
    this.modalService.open({
      component: LoginModalComponent,
      width: '500px',
      title: 'Connexion',
      closable: true,
    });
  }

  setDefaultCountry(country: Country) {
    // Use CountryStorageService to properly notify all subscribers
    // Le changement de thème est géré automatiquement dans CountryStorageService
    this.countryStorage.setCountry(country);

    this.showCountryDropdown = false;
    this.router
      .navigate(this.urlService.buildRouteSegments('home'))
      .then(() => {
        // Optionally reload if you need to reset state
        window.location.reload();
      });
  }

  toggleCountryDropdown() {
    // Ne pas permettre l'ouverture du dropdown si le changement de pays est désactivé
    if (this.isCountryChangeDisabled()) {
      return;
    }
    this.showCountryDropdown = !this.showCountryDropdown;
  }

  /**
   * Vérifie si le changement de pays est désactivé (sous-domaine Orange)
   * @returns true si le changement de pays est désactivé
   */
  isCountryChangeDisabled(): boolean {
    return this.countryStorage.isCountryChangeDisabled();
  }

  /**
   * Obtient le thème actuel
   */
  getCurrentTheme(): Theme {
    return this.themeService.getCurrentTheme();
  }

  /**
   * Détermine la classe CSS pour le fond de la navbar selon le thème et la route
   */
  getNavbarBgClass(): string {
    const theme = this.getCurrentTheme();
    const isDashboard = this.isPassportRoute;

    // MSAS : rouge à l'accueil, noir au dashboard
    if (theme === 'msas') {
      return isDashboard ? 'navbar-dark' : 'navbar-primary';
    }

    // CI : orange
    if (theme === 'ci') {
      return 'navbar-primary';
    }

    // Orange : orange
    if (theme === 'orange') {
      return 'navbar-primary';
    }

    // DPP : blanc
    if (theme === 'dpp') {
      return 'navbar-primary';
    }

    // Bourkina : rouge
    if (theme === 'bourkina') {
      return 'navbar-primary';
    }

    // Eyone et autres : primaire par défaut
    return 'navbar-primary';
  }

  /**
   * Détermine la classe CSS pour le texte de la navbar
   */
  getNavbarTextClass(): string {
    const theme = this.getCurrentTheme();
    const isDashboard = this.isPassportRoute;

    // MSAS : texte blanc à l'accueil (rouge), blanc au dashboard (noir)
    if (theme === 'msas') {
      return 'text-white';
    }

    // DPP : texte sombre (fond blanc)
    if (theme === 'dpp') {
      return 'text-gray-800';
    }

    // CI, Orange, Bourkina : texte blanc (fond coloré)
    if (theme === 'ci' || theme === 'orange' || theme === 'bourkina') {
      return 'text-white';
    }

    // Eyone et autres : texte blanc par défaut (fond sombre)
    return 'text-white';
  }

  /**
   * Détermine si la navbar doit utiliser le style simple (comme DPP) ou complexe
   */
  useSimpleNavbarStyle(): boolean {
    const theme = this.getCurrentTheme();
    return theme === 'dpp';
  }

  /**
   * Obtient le chemin du logo selon le thème
   */
  getLogoPath(): string {
    const theme = this.getCurrentTheme();
    const isDashboard = this.isPassportRoute;

    // console.log('theme', theme);

    if (theme === 'dpp') {
      return 'images/logos/msas.jpg';
    }

    if (theme === 'ci') {
      return 'images/logos/logo_sm.svg';
    }

    if (theme === 'orange') {
      return 'images/logos/logo-orange-bg-white.jpg';
    }

    if (theme === 'fajma') {
      return 'images/logos/logo-fajma-bg-white.png';
    }

    // Pour les autres thèmes, utiliser logo-light.png pour navbar sombre
    if (this.getNavbarBgClass() === 'navbar-dark') {
      return 'images/logo-light.png';
    }

    // Pour navbar colorée, utiliser logo-light.png
    return 'images/logo-light.png';
  }

  /**
   * Obtient le titre de la navbar selon le thème
   */
  getNavbarTitle(): string {
    const theme = this.getCurrentTheme();

    if (theme === 'ci') {
      return 'Pass Santé Mousso';
    }
    if (theme === 'fajma') {
      return this.themeService.getProductName();
    }

    // Pour les autres thèmes, retourner la traduction
    return this.translate.instant('HEADER.MEDICAL_PASSPORT');
  }
}
